import type { ReactNode, CSSProperties } from "react";
import { useAuth } from "../components/AuthProvider";
import { useGetEJOQuery } from "../hooks/useEJOApproval";
import { useGetAllESR } from "../hooks/useESRApproval";
import {
  useGetEPFERFQuery,
  useGetEXFQuery,
  useGetEORRQuery,
} from "../hooks/useEPFERF";

import HomeIcon from "@mui/icons-material/Home";
import UsersIcon from "@mui/icons-material/Group";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReportIcon from "@mui/icons-material/Assessment";
import DatabaseIcon from "@mui/icons-material/StorageSharp";
import CircleIcon from "@mui/icons-material/Circle";
import { appColors } from "../theme";
import { useGetPRQuery } from "./usePR";

const { primary, sidebarNavActiveBg } = appColors;

export const activeStyles: CSSProperties = {
  backgroundColor: sidebarNavActiveBg,
  color: primary,
};

export const subItemActiveStyles: CSSProperties = {
  backgroundColor: sidebarNavActiveBg,
  color: primary,
};

export interface SidebarMenu {
  label: string;
  icon?: ReactNode;
  badge?: number;
  path?: string;
  subItems?: SidebarMenu[];
}

const useSidebarMenuItems = () => {
  const user = useAuth();
  const role = user && user?.currentUserRole?.name;
  const rolePermissions = user && user?.currentUserRole?.permissions;

  const hasUserAccess = rolePermissions?.some((permission) =>
    permission.startsWith("USER")
  );

  const hasRoleAccess = rolePermissions?.some((permission) =>
    permission.startsWith("ROLE")
  );

  const hasEurAccess = rolePermissions?.some((permission) =>
    permission.startsWith("EUR")
  );

  const hasEcAccess = rolePermissions?.some((permission) =>
    permission.startsWith("EC")
  );

  const hasOlAccess = rolePermissions?.some((permission) =>
    permission.startsWith("OL")
  );

  const hasRepDauiAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::DAUI")
  );

  const hasRepHeurAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::HEUR")
  );

  const hasRepHrentAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::HRENT")
  );

  const hasRepCpcAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::CPC")
  );

  const hasRepClcAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::CLC")
  );

  const hasRepFcAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::FC")
  );

  const hasRepEstAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::EST")
  );

  const hasRepEmAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REP::EM")
  );

  const hasMmdEqAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::EQ")
  );

  const hasMmdEcAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::EC")
  );

  const hasMmdPmsAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::PMS")
  );

  const hasMmdPctAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::PCT")
  );

  const hasMmdPdAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::PD")
  );

  const hasMmdPartsAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::PARTS")
  );

  const hasMmdSrtAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::SRT")
  );

  const hasMmdTtAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::TT")
  );

  const hasMmdLtAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::LT")
  );

  const hasMmdMechAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::MECH")
  );

  const hasMmdOpAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::OP")
  );

  const hasMmdOcAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::OC")
  );

  const hasMmdFtAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::FT")
  );

  const hasMmdLocAccess = rolePermissions?.some((permission) =>
    permission.startsWith("MMD::LOC")
  );

  const hasEjoManagementAccess = rolePermissions?.some((permission) =>
    permission.startsWith("EJO::MANAGEMENT")
  );

  const hasEjoEsrApprovalAccess = rolePermissions?.some((permission) =>
    permission.startsWith("EJO::ESR::APPROVAL")
  );

  const hasEsrManagementAccess = rolePermissions?.some((permission) =>
    permission.startsWith("ESR::MANAGEMENT")
  );

  const hasFrisManagementAccess = rolePermissions?.some((permission) =>
    permission.startsWith("FRIS::MANAGEMENT")
  );

  const hasReqManagementAccess = rolePermissions?.some((permission) =>
    permission.startsWith("REQ::MANAGEMENT")
  );

  const hasAllocManagementAccess = rolePermissions?.some((permission) =>
    permission.startsWith("ALLOC::MANAGEMENT")
  );

  const erErf = useGetEPFERFQuery({
    role: hasReqManagementAccess ? "REQ::MANAGEMENT" : hasAllocManagementAccess ? "ALLOC::MANAGEMENT" : "",
    page: "ERF",
  });

  const erAllocations = useGetEPFERFQuery({
    role: hasReqManagementAccess ? "REQ::MANAGEMENT" : hasAllocManagementAccess ? "ALLOC::MANAGEMENT" : "",
    page: "Allocations",
    status: "Allocated"
  });

  const erPr = useGetPRQuery();

  const erExf = useGetEXFQuery();

  const erEorr = useGetEORRQuery();

  const ejoData = useGetEJOQuery("Unapproved");

  const esrData = useGetAllESR("Unapproved", "");

  const esrUncreatedData = useGetEJOQuery(
    "Approved",
    "",
    user?.authUser?.id || "undefined",
    false
  );

  const esrWipData = useGetAllESR(
    "Approved",
    user?.authUser?.id || "undefined"
  );

  let erErfCount = 0;
  let erAllocationsCount = 0;
  let erPrCount = 0;
  let erExfCount = 0;
  let erEorrCount = 0;

  let ejoCount = 0;
  let esrCount = 0;
  let esrUncreatedCount = 0;
  let esrWipCount = 0;

  let ejoEsrBadgeCount = 0;
  let erBadgeCount = 0;

  if (hasAllocManagementAccess) {
    erErfCount = erErf?.data?.length || 0;

    erEorrCount = erEorr?.data
      ? erEorr.data.filter((row) => row.eorrPickUpNo === undefined).length
      : 0;

    erAllocationsCount = erAllocations?.data?.length || 0;

    erPrCount = erPr?.data?.filter((row) => row.prStatus === "Open").length || 0;

    erExfCount = erExf?.data?.filter((row) => row.exfStatus === "").length || 0;

    erBadgeCount = erErfCount + erAllocationsCount + erPrCount + erExfCount + erEorrCount;
  } else if (hasReqManagementAccess) {
    erErfCount = erErf?.data
      ? erErf.data.filter((row) => row.status === "In Transit").length
      : 0;

    erEorrCount = erEorr?.data
      ? erEorr.data.filter((row) => row.eorrPickUpNo !== undefined).length
      : 0;

    erBadgeCount = erErfCount + erEorrCount;
  }

  if (
    rolePermissions &&
    rolePermissions.some((permission) =>
      ["EJO::ESR::APPROVAL"].includes(permission)
    )
  ) {
    ejoCount = ejoData?.data?.length || 0;
    esrCount = esrData?.data?.length || 0;
    ejoEsrBadgeCount = ejoCount + esrCount;
  } else if (
    rolePermissions &&
    rolePermissions.some((permission) =>
      ["ESR::MANAGEMENT"].includes(permission)
    )
  ) {
    esrUncreatedCount = esrUncreatedData?.data?.length || 0;
    esrWipCount = esrWipData?.data?.length || 0;
    ejoEsrBadgeCount = esrUncreatedCount + esrWipCount;
  }

  const menu: SidebarMenu[] = [
    {
      label: "Homepage",
      icon: <HomeIcon fontSize="small" />,
      path: "/home/all",
    },
    ...(hasUserAccess || hasRoleAccess
      ? [
          {
            label: "Manage My Users",
            icon: <UsersIcon fontSize="small" />,
            subItems: [
              ...(hasUserAccess
                ? [
                    {
                      label: "Users",
                      path: "/users",
                    },
                  ]
                : []),
              ...(hasRoleAccess
                ? [
                    {
                      label: "Roles and Permissions",
                      path: "/roles",
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(hasReqManagementAccess ||
    hasAllocManagementAccess ||
    hasEurAccess ||
    hasEcAccess ||
    hasOlAccess ||
    hasEjoManagementAccess ||
    hasEjoEsrApprovalAccess ||
    hasEsrManagementAccess ||
    hasFrisManagementAccess
      ? [
          {
            label: "View My Dashboards + Tasks",
            icon: <DashboardIcon fontSize="small" />,
            badge: erBadgeCount + ejoEsrBadgeCount,
            subItems: [
              ...(hasReqManagementAccess || hasAllocManagementAccess
                ? [
                    {
                      label: "My Equipment Requests",
                      badge: erBadgeCount,
                      subItems:
                        hasAllocManagementAccess
                          ? [
                              {
                                label: "My ERFs",
                                path: "/my-equipment-requests/my-erf",
                                badge: erErfCount,
                              },
                              {
                                label: "My Allocations",
                                path: "/my-equipment-requests/my-allocations",
                                badge: erAllocationsCount,
                              },
                              {
                                label: "My In Transits",
                                path: "/my-equipment-requests/my-in-transits",
                              },
                              {
                                label: "My PRs",
                                path: "/my-equipment-requests/my-pr",
                                badge: erPrCount,
                              },
                              {
                                label: "My EXFs",
                                path: "/my-equipment-requests/my-exf",
                                badge: erExfCount,
                              },
                              {
                                label: "My EORRs",
                                path: "/my-equipment-requests/my-eorr",
                                badge: erEorrCount,
                              },
                            ]
                          : [
                              {
                                label: "My ERFs",
                                path: "/my-equipment-requests/my-erf",
                                badge: erErfCount,
                              },
                              {
                                label: "My EXFs",
                                path: "/my-equipment-requests/my-exf",
                              },
                              {
                                label: "My EORRs",
                                path: "/my-equipment-requests/my-eorr",
                                badge: erEorrCount,
                              },
                            ],
                    },
                    {
                      label: "My On Rent Equipment",
                      path: "/my-on-rent-equipment",
                    },
                  ]
                : []),
              ...(hasEurAccess
                ? [
                    {
                      label: "My EURs",
                      path: "/eur",
                    },
                  ]
                : []),
              ...(hasEcAccess
                ? [
                    {
                      label: "My Equipment Certificates",
                      path: "/equipment-certificates",
                    },
                  ]
                : []),
              ...(hasOlAccess
                ? [
                    {
                      label: "My Operator Licenses",
                      path: "/operator-licenses",
                    },
                  ]
                : []),
              ...(hasEjoManagementAccess
                ? [
                    {
                      label: "MRRF Management",
                      path: "/ejo-management",
                    },
                  ]
                : []),
              ...(hasEjoEsrApprovalAccess
                ? [
                    {
                      label: "MRRF/JO Approval",
                      badge: ejoEsrBadgeCount,
                      subItems: [
                        {
                          label: "MRRF Pending Approval",
                          path: "/ejo-and-esr-approval/ejo-pending",
                          badge: ejoCount,
                        },
                        {
                          label: "MRRF Approved > JO Uncreated",
                          path: "/ejo-and-esr-approval/ejo-approved-and-esr-uncreated",
                        },
                        {
                          label: "JO Pending Approval",
                          path: "/ejo-and-esr-approval/esr-pending-approval",
                          badge: esrCount,
                        },
                        // {
                        //   label: "JO Approved > Unstarted",
                        //   path: "/ejo-and-esr-approval/esr-approved-and-unstarted",
                        // },
                        {
                          label: "JO WIP",
                          path: "/ejo-and-esr-approval/esr-wip",
                        },
                      ],
                    },
                  ]
                : []),
              ...(hasEsrManagementAccess
                ? [
                    {
                      label: "JO Management",
                      badge: ejoEsrBadgeCount,
                      subItems: [
                        {
                          label: "MRRF Approved > JO Uncreated",
                          path: "/esr-management/ejo-approved-and-esr-uncreated",
                          badge: esrUncreatedCount,
                        },
                        {
                          label: "JO Pending Approval",
                          path: "/esr-management/esr-pending-approval",
                        },
                        // {
                        //   label: "JO Approved > Unstarted",
                        //   path: "/esr-management/esr-approved-and-unstarted",
                        // },
                        {
                          label: "JO WIP",
                          path: "/esr-management/esr-wip",
                          badge: esrWipCount,
                        },
                      ],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(hasRepDauiAccess ||
    hasRepHeurAccess ||
    hasRepHrentAccess ||
    hasRepCpcAccess ||
    hasRepClcAccess ||
    hasRepFcAccess ||
    hasRepEstAccess ||
    hasRepEmAccess
      ? [
          {
            label: "Get My Reports",
            icon: <ReportIcon fontSize="small" />,
            subItems: [
              ...(hasRepDauiAccess || hasRepHeurAccess || hasRepHrentAccess
                ? [
                    {
                      label: "Equipment Use & Performance Reports",
                      subItems: [
                        ...(hasRepDauiAccess
                          ? [
                              {
                                label:
                                  "Down, Availability Utilization, Idle (DAIU) Report",
                                path: "/equipment-use-and-performance-reports/daui",
                              },
                            ]
                          : []),
                        ...(hasRepHeurAccess
                          ? [
                              {
                                label: "Historical EURs",
                                path: "/equipment-use-and-performance-reports/historical-eurs",
                              },
                            ]
                          : []),
                        ...(hasRepHrentAccess
                          ? [
                              {
                                label: "Historical Rentals",
                                path: "/equipment-use-and-performance-reports/historical-rentals",
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(hasRepCpcAccess ||
              hasRepClcAccess ||
              hasRepFcAccess ||
              hasRepEstAccess ||
              hasRepEmAccess
                ? [
                    {
                      label: "Equipment Cost Reports",
                      subItems: [
                        ...(hasRepCpcAccess
                          ? [
                              {
                                label: "Cost of Parts Consumed (CPC) Report",
                                path: "/equipment-cost-reports/cpc",
                              },
                            ]
                          : []),
                        ...(hasRepClcAccess
                          ? [
                              {
                                label: "Cost of Labor Consumed (CLC) Report",
                                path: "/equipment-cost-reports/clc",
                              },
                            ]
                          : []),
                        ...(hasRepFcAccess
                          ? [
                              {
                                label: "Equipment Fuel Consumption",
                                path: "/equipment-cost-reports/equipment-fuel-consumption",
                              },
                            ]
                          : []),
                        ...(hasRepEstAccess
                          ? [
                              {
                                label: "MRRF-JO Timeline Report",
                                path: "/equipment-cost-reports/eet",
                              },
                            ]
                          : []),
                        ...(hasRepEmAccess
                          ? [
                              {
                                label: "MRRF Manpower Report",
                                path: "/equipment-cost-reports/ejo-manpower",
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(hasMmdEqAccess ||
    hasMmdEcAccess ||
    hasMmdPmsAccess ||
    hasMmdPctAccess ||
    hasMmdPdAccess ||
    hasMmdPartsAccess ||
    hasMmdSrtAccess ||
    hasMmdTtAccess ||
    hasMmdLtAccess ||
    hasMmdMechAccess ||
    hasMmdOpAccess ||
    hasMmdOcAccess ||
    hasMmdFtAccess ||
    hasMmdLocAccess
      ? [
          {
            label: "Manage My Databases",
            icon: <DatabaseIcon fontSize="small" />,
            subItems: [
              ...(hasMmdEqAccess || hasMmdEcAccess
                ? [
                    {
                      label: "Equipment Databases",
                      subItems: [
                        ...(hasMmdEqAccess
                          ? [
                              {
                                label: "Equipment Master",
                                path: "/equipment",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdEcAccess
                          ? [
                              {
                                label: "HE Certificate Type Master",
                                path: "/he-certificate-type",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(hasMmdPmsAccess ||
              hasMmdPctAccess ||
              hasMmdPdAccess ||
              hasMmdPartsAccess ||
              hasMmdSrtAccess ||
              hasMmdTtAccess ||
              hasMmdLtAccess
                ? [
                    {
                      label: "Servicing Databases",
                      subItems: [
                        ...(hasMmdPmsAccess
                          ? [
                              {
                                label: "PMS Code Master",
                                path: "/pms-code",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdPctAccess
                          ? [
                              {
                                label: "PMS Code + Task + Parts Master",
                                path: "/pms-code-task-parts",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdPdAccess
                          ? [
                              {
                                label: "Parts Description",
                                path: "/parts-description",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdPartsAccess
                          ? [
                              {
                                label: "Parts Master",
                                path: "/parts",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdSrtAccess
                          ? [
                              {
                                label: "Service Request Type Master",
                                path: "/service-request-type",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdTtAccess
                          ? [
                              {
                                label: "Service Task Master",
                                path: "/service-task",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdLtAccess
                          ? [
                              {
                                label: "Labor Type + Rate Master",
                                path: "/labor-type-rate",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              // {
              //   label: "Project Databases",
              //   subItems: [
              //     {
              //       label: "Project Master",
              //       path: "/project",
              //       icon: (
              //         <CircleIcon
              //           fontSize="small"
              //           sx={{ transform: "scale(0.4)" }}
              //         />
              //       ),
              //     },
              //     {
              //       label: "Project Activity + Cost Code Master",
              //       path: "/project-activity-cost-code",
              //       icon: (
              //         <CircleIcon
              //           fontSize="small"
              //           sx={{ transform: "scale(0.4)" }}
              //         />
              //       ),
              //     },
              //     {
              //       label: "Equipment Planned Shift Hours",
              //       path: "/equipment-planned-shift-hours",
              //       icon: (
              //         <CircleIcon
              //           fontSize="small"
              //           sx={{ transform: "scale(0.4)" }}
              //         />
              //       ),
              //     },
              //   ],
              // },
              ...(hasMmdMechAccess || hasMmdOpAccess || hasMmdOcAccess
                ? [
                    {
                      label: "Personnel Databases",
                      subItems: [
                        ...(hasMmdMechAccess
                          ? [
                              {
                                label: "Mechanic Master",
                                path: "/mechanic",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdOpAccess
                          ? [
                              {
                                label: "HE Operator Master",
                                path: "/he-operator",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdOcAccess
                          ? [
                              {
                                label: "HE Operator Certificate Type Master",
                                path: "/he-operator-certificate-type",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(hasMmdFtAccess || hasMmdLocAccess
                ? [
                    {
                      label: "Other Databases",
                      subItems: [
                        // {
                        //   label: "Meter Type + Interval Type Master",
                        //   path: "/meter-type-interval-type",
                        //   icon: (
                        //     <CircleIcon
                        //       fontSize="small"
                        //       sx={{ transform: "scale(0.4)" }}
                        //     />
                        //   ),
                        // },
                        ...(hasMmdFtAccess
                          ? [
                              {
                                label: "Fuel Type Master",
                                path: "/fuel-type",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasMmdLocAccess
                          ? [
                              {
                                label: "Location Master",
                                path: "/location",
                                icon: (
                                  <CircleIcon
                                    fontSize="small"
                                    sx={{ transform: "scale(0.4)" }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        // {
                        //   label: "Rental Rates Master",
                        //   path: "/rental-rates",
                        //   icon: (
                        //     <CircleIcon
                        //       fontSize="small"
                        //       sx={{ transform: "scale(0.4)" }}
                        //     />
                        //   ),
                        // },
                        // {
                        //   label: "Fuel (Per Liter) Update Master",
                        //   path: "/fuel-update",
                        //   icon: (
                        //     <CircleIcon
                        //       fontSize="small"
                        //       sx={{ transform: "scale(0.4)" }}
                        //     />
                        //   ),
                        // },
                        // {
                        //   label: "Fuel Cost Code Master",
                        //   path: "/fuel-cost-code",
                        //   icon: (
                        //     <CircleIcon
                        //       fontSize="small"
                        //       sx={{ transform: "scale(0.4)" }}
                        //     />
                        //   ),
                        // },
                      ],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  return menu;
};

export default useSidebarMenuItems;
